import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { COMPANY, NAV, ASSETS } from "@/data/site";

const linkBase =
  "px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-volt focus-visible:text-volt rounded-md";

function DesktopDropdown({ label, items, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testid}
        className={`${linkBase} inline-flex items-center gap-1 outline-none data-[state=open]:text-volt`}
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-pine border-white/10 text-slate-200 min-w-[240px]">
        {items.map((item) => (
          <DropdownMenuItem key={item.to} asChild className="focus:bg-moss focus:text-volt cursor-pointer">
            <Link to={item.to} data-testid={`nav-${testid}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span>
                <span className="block font-medium">{item.label}</span>
                {item.desc && <span className="block text-xs text-slate-400">{item.desc}</span>}
              </span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileGroup({ label, items }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-volt/70 mb-2">{label}</p>
      <div className="flex flex-col gap-1">
        {items.map((item) => (
          <SheetClose asChild key={item.to}>
            <Link
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="rounded-md px-3 py-2.5 text-base font-medium text-slate-200 hover:bg-moss hover:text-volt transition-colors"
            >
              {item.label}
            </Link>
          </SheetClose>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-obsidian/90 backdrop-blur-xl">
      <nav aria-label="Primary" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center" aria-label={`${COMPANY.name} — home`}>
            <img
              src={ASSETS.logo}
              alt={`${COMPANY.name} official logo`}
              className="h-10 w-10 lg:h-12 lg:w-12 rounded-md ring-1 ring-white/15"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            <NavLink to="/about" data-testid="nav-link-about" className={linkBase}>About</NavLink>
            <NavLink to="/technology" data-testid="nav-link-technology" className={linkBase}>Technology</NavLink>
            <NavLink
              to="/solar"
              data-testid="nav-link-solar"
              className="px-3 py-2 text-sm font-semibold text-volt transition-colors hover:text-volt-soft focus-visible:text-volt rounded-md"
            >
              Solar Calculator
            </NavLink>
            <NavLink
              to="/energy"
              data-testid="nav-link-energy"
              className="px-3 py-2 text-sm font-semibold text-volt transition-colors hover:text-volt-soft focus-visible:text-volt rounded-md"
            >
              Energy Intelligence
            </NavLink>
            <DesktopDropdown label="Products" items={NAV.products} testid="nav-products-dropdown" />
            <NavLink to="/projects" data-testid="nav-link-projects" className={linkBase}>Projects</NavLink>
            <NavLink to="/sustainability" data-testid="nav-link-sustainability" className={linkBase}>Sustainability</NavLink>
            <DesktopDropdown label="Company" items={NAV.company} testid="nav-company-dropdown" />
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="ml-3 inline-flex items-center rounded-md bg-volt px-5 py-2.5 text-sm font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian active:translate-y-0"
            >
              Contact
            </Link>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label={open ? "Close menu" : "Open menu"}
                className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-md border border-white/15 text-white hover:text-volt hover:border-volt/50 transition-colors"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-obsidian border-white/10 w-[86vw] sm:max-w-sm p-6 overflow-y-auto">
              <div className="flex items-center gap-3 mb-8 mt-2">
                <img src={ASSETS.logo} alt="" className="h-10 w-10 rounded-md ring-1 ring-white/15" />
                <span className="font-display font-extrabold text-white">BNM Sustainable Energy</span>
              </div>
              <nav aria-label="Mobile" className="flex flex-col gap-6" key={location.pathname}>
                <SheetClose asChild>
                  <Link
                    to="/solar"
                    data-testid="mobile-nav-solar"
                    className="flex items-center justify-between rounded-md border border-volt/40 bg-volt/10 px-4 py-3.5 font-semibold text-volt"
                  >
                    Solar Energy Calculator
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-volt/70">Live</span>
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to="/energy"
                    data-testid="mobile-nav-energy"
                    className="flex items-center justify-between rounded-md border border-volt/40 bg-volt/10 px-4 py-3.5 font-semibold text-volt"
                  >
                    Energy Intelligence & Solutions
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-volt/70">New</span>
                  </Link>
                </SheetClose>
                <MobileGroup label="Company" items={[{ label: "Home", to: "/" }, { label: "About BNM", to: "/about" }, ...NAV.company]} />
                <MobileGroup label="Technology" items={[{ label: "Technology", to: "/technology" }, ...NAV.products]} />
                <MobileGroup label="Impact" items={[{ label: "Projects & Impact", to: "/projects" }, { label: "Sustainability", to: "/sustainability" }]} />
                <SheetClose asChild>
                  <Link
                    to="/contact"
                    data-testid="mobile-nav-contact"
                    className="mt-2 inline-flex items-center justify-center rounded-md bg-volt px-5 py-3 font-semibold text-obsidian hover:bg-volt-soft hover:text-obsidian transition-colors"
                  >
                    Contact Us
                  </Link>
                </SheetClose>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
